import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useContext } from "react";
import { collection, doc, updateDoc, getDoc, getDocs, where, query } from "firebase/firestore";
import { db } from '../../firebase';
import { AuthContext } from "../../context/AuthContext";
import JSZip from 'jszip';
import { Button, CircularProgress } from '@mui/material';
import {
    getFolderName, downloadSelectedDocumentsAsPDF,
    downloadSelectedPhotos, downloadSelectedCSV, getCSVBlobForDocuments,
    formatDate, DownloadModal, formatTimeRemaining
} from './documentUtils';
import { getPdfDesignForDocument } from '../forms/formHelpers/pdfDesignHelper';
import { uploadLogToFirestore, uploadErrorLogToFirestore, uploadProfilingLogToFirestore } from '../../utils/logUtils.js';
import { ActionButtons, Table } from './ReportList';
import NewFilterBar from './utils/NewFilterBar';
import { fetchFilteredDocuments, fetchMoreFilteredDocuments, fetchAllFilteredDocuments } from './utils/documentService';
import tableProfiler from './utils/TablePerformanceProfiler';
import './table.scss';

// Tamaño de página para la carga de documentos (~1000 para tramos grandes)
const ITEMS_PER_PAGE = 1000;

/**
 * Componente TableDocuments - Versión optimizada de la tabla de documentos.
 * @returns {JSX.Element} - Elemento de React que representa la tabla de documentos.
 */
const TableDocuments = () => {
    // Estados para los datos y filtros
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [allRowsSelected, setAllRowsSelected] = useState(false);
    const [deletedRows, setDeletedRows] = useState([]);

    // Estados para los nuevos filtros
    const [clienteFilter, setClienteFilter] = useState("");
    const [tipoFilter, setTipoFilter] = useState("");
    const [tipFilter, setTipFilter] = useState("");
    const [fechaInicioFilter, setFechaInicioFilter] = useState("");
    const [fechaFinFilter, setFechaFinFilter] = useState("");

    // Estados para la paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    // Estados de carga
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Cargando documentos...");
    const [nombresLoaded, setNombresLoaded] = useState(false);

    // Estados para la descarga
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloadFeedback, setDownloadFeedback] = useState(null);
    const [isDownloadModalOpen, setDownloadModalOpen] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [downloadStats, setDownloadStats] = useState(null);
    
    // Refs para el cálculo del tiempo estimado
    const downloadStartTimeRef = useRef(null);
    const lastProgressUpdateRef = useRef({ time: null, progress: 0 });

    // Contexto y parámetros
    const { tipVS } = useParams();
    const { currentUser, currentService, adminData } = useContext(AuthContext);

    // Control de componente montado
    const mounted = useRef(true);

    // Caché de vigilantes
    const vigilantesCache = useRef(new Map());

    // Limpieza al desmontar
    useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);

    /**
     * Recupera y agrega el nombre del vigilante a cada documento.
     * @param {Array} rowsToProcess - Filas a procesar
     * @returns {Promise<Array>} - Filas procesadas con nombres
     */
    const populateNombre = useCallback(async (rowsToProcess) => {
        if (!rowsToProcess || rowsToProcess.length === 0 || nombresLoaded) {
            return rowsToProcess;
        }

        try {
            // Obtener solo los documentos que no tienen nombre y tienen vigilante
            const docsWithoutInfo = rowsToProcess.filter(doc =>
                !doc.nombre && doc.vigilante && !vigilantesCache.current.has(doc.vigilante)
            );

            if (docsWithoutInfo.length === 0) {
                // Aplicar información de caché a los documentos
                return rowsToProcess.map(doc => {
                    if (!doc.vigilante) return doc;

                    const vigilanteInfo = vigilantesCache.current.get(doc.vigilante);
                    if (!vigilanteInfo) return doc;

                    return {
                        ...doc,
                        nombre: vigilanteInfo.nombre,
                        vigilanteEmail: vigilanteInfo.email
                    };
                });
            }

            // Obtener emails únicos de vigilantes
            const vigilanteEmails = [...new Set(docsWithoutInfo
                .map(doc => doc.vigilante)
                .filter(Boolean))];

            if (vigilanteEmails.length === 0) {
                return rowsToProcess;
            }

            // Obtener el servicio
            const serviciosRef = collection(db, 'servicios');
            const q = query(serviciosRef, where('admins', 'array-contains', currentUser.email));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                return rowsToProcess;
            }

            const servicioId = querySnapshot.docs[0].id;

            // Dividir en lotes de 10 (límite de Firestore para consultas 'in')
            const BATCH_SIZE = 10;

            // Procesar lotes con delay para evitar sobrecarga
            for (let i = 0; i < vigilanteEmails.length; i += BATCH_SIZE) {
                const emailBatch = vigilanteEmails.slice(i, i + BATCH_SIZE);

                // Añadir delay entre lotes para evitar sobrecarga
                if (i > 0) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

                // Obtener datos de vigilantes
                const vigilantesQuery = query(
                    collection(db, 'servicios', servicioId, 'vigilantes'),
                    where('email', 'in', emailBatch)
                );

                const vigilantesSnapshot = await getDocs(vigilantesQuery);

                vigilantesSnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    vigilantesCache.current.set(data.email, {
                        nombre: `${data.nombre || ''} ${data.apellidos || ''}`.trim(),
                        email: data.email
                    });
                });
            }

            // Actualizar documentos
            const updatedRows = rowsToProcess.map(doc => {
                if (!doc.vigilante) return doc;

                const vigilanteInfo = vigilantesCache.current.get(doc.vigilante);
                if (!vigilanteInfo) return doc;

                return {
                    ...doc,
                    nombre: vigilanteInfo.nombre,
                    vigilanteEmail: vigilanteInfo.email
                };
            });

            return updatedRows;

        } catch (error) {
            console.error('Error al obtener nombres de vigilantes:', error);
            uploadErrorLogToFirestore('Error en proceso de nombres:', error);
            return rowsToProcess;
        }
    }, [currentUser.email, nombresLoaded]);

    /**
     * Carga inicial de documentos.
     */
    useEffect(() => {
        const loadInitialData = async () => {
            if (!currentService?.id || !adminData) return;

            setIsLoading(true);
            setLoadingMessage("Cargando documentos iniciales...");

            // 📊 Iniciar sesión de profiling
            tableProfiler.startSession('TableDocuments_InitialLoad', {
                serviceId: currentService.id,
                pageSize: ITEMS_PER_PAGE,
                userId: currentUser?.uid
            });

            try {
                // 📊 Medir carga de documentos
                tableProfiler.startMeasurement('01_FetchInitialDocuments', {
                    pageSize: ITEMS_PER_PAGE
                });

                const result = await fetchFilteredDocuments({
                    serviceId: currentService.id,
                    filters: {},
                    pageSize: ITEMS_PER_PAGE,
                    populateNombreFunc: populateNombre
                });

                tableProfiler.endMeasurement('01_FetchInitialDocuments', {
                    documentsLoaded: result.documents.length,
                    hasMore: result.hasMore
                });

                // 📊 Medir renderizado
                tableProfiler.startMeasurement('02_RenderDocuments');

                setRows(result.documents);
                setLastDoc(result.lastDoc);
                setHasMore(result.hasMore);
                setCurrentPage(1);

                // Esperar renderizado
                setTimeout(() => {
                    tableProfiler.endMeasurement('02_RenderDocuments', {
                        documentsRendered: result.documents.length
                    });

                    // 📊 Finalizar sesión
                    tableProfiler.endSession({
                        totalDocuments: result.documents.length,
                        hasMorePages: result.hasMore,
                        success: true
                    });
                }, 0);

            } catch (error) {
                console.error('Error cargando documentos iniciales:', error);
                uploadErrorLogToFirestore('Error al cargar documentos iniciales: ' + error);
                
                // Finalizar sesión con error
                tableProfiler.endSession({
                    error: error.message,
                    success: false
                });
            } finally {
                setIsLoading(false);
            }
        };

        if (currentService?.id && adminData) {
            loadInitialData();
        }
    }, [currentService?.id, adminData, populateNombre, currentUser?.uid]);

    /**
     * Actualizar cuando cambia el tipo de servicio.
     */
    useEffect(() => {
        if (mounted.current && currentService?.id && adminData) {
            setLastDoc(null);
            setHasMore(true);
            setRows([]);

            // Resetear filtros
            setClienteFilter("");
            setTipoFilter("");
            setTipFilter("");
            setFechaInicioFilter("");
            setFechaFinFilter("");

            // Cargar datos iniciales
            fetchFilteredDocuments({
                serviceId: currentService.id,
                filters: {},
                pageSize: ITEMS_PER_PAGE,
                populateNombreFunc: populateNombre
            }).then(result => {
                setRows(result.documents);
                setLastDoc(result.lastDoc);
                setHasMore(result.hasMore);
                setCurrentPage(1);
            }).catch(error => {
                console.error('Error al cambiar tipo de servicio:', error);
                uploadErrorLogToFirestore('Error al cambiar tipo de servicio: ' + error);
            });
        }
    }, [tipVS, currentService?.id, adminData, populateNombre]);

    /**
     * Maneja la búsqueda con los filtros aplicados.
     * Carga todos los resultados que coincidan con los filtros.
     */
    const handleSearch = async () => {
        if (!currentService?.id || !adminData || isLoading) return;

        setIsLoading(true);
        setLoadingMessage("Buscando documentos...");

        // Crear objeto de filtros
        const filters = {
            cliente: clienteFilter.trim(),
            tipo: tipoFilter.trim(),
            tip: tipFilter.trim(),
            fechaInicio: fechaInicioFilter,
            fechaFin: fechaFinFilter
        };

        // Verificar si hay al menos un filtro activo
        const hasActiveFilters = Object.values(filters).some(value =>
            value && (typeof value !== 'string' || value.trim() !== '')
        );

        // 📊 Iniciar sesión de profiling
        tableProfiler.startSession('TableDocuments_Search', {
            serviceId: currentService.id,
            hasActiveFilters,
            filters: hasActiveFilters ? filters : 'none'
        });

        try {
            // Resetear paginación
            setCurrentPage(1);
            setLastDoc(null);
            setHasMore(false); // No usamos paginación para búsquedas filtradas

            if (hasActiveFilters) {
                // Mostrar mensaje de progreso
                setLoadingMessage("Cargando todos los resultados que coinciden con los filtros...");

                // 📊 Medir búsqueda completa
                tableProfiler.startMeasurement('03_SearchAllDocuments', {
                    filters,
                    filtersCount: Object.values(filters).filter(v => v).length
                });

                // Utilizar la función para cargar todos los documentos filtrados
                const allDocuments = await fetchAllFilteredDocuments({
                    serviceId: currentService.id,
                    filters,
                    populateNombreFunc: populateNombre,
                    progressCallback: (progress) => {
                        setLoadingMessage(`Cargando documentos... (${progress.total} encontrados)`);
                    }
                });

                tableProfiler.endMeasurement('03_SearchAllDocuments', {
                    documentsFound: allDocuments.length
                });

                setRows(allDocuments);
                // No hay más paginación porque ya cargamos todo
                setHasMore(false);
                setLastDoc(null);

                // 📊 Finalizar sesión
                tableProfiler.endSession({
                    documentsFound: allDocuments.length,
                    hasActiveFilters: true,
                    success: true
                });
            } else {
                // Sin filtros, cargar normalmente la primera página
                // 📊 Medir carga sin filtros
                tableProfiler.startMeasurement('04_LoadNoFilters', {
                    pageSize: ITEMS_PER_PAGE
                });

                const result = await fetchFilteredDocuments({
                    serviceId: currentService.id,
                    filters: {},
                    pageSize: ITEMS_PER_PAGE,
                    populateNombreFunc: populateNombre
                });

                tableProfiler.endMeasurement('04_LoadNoFilters', {
                    documentsLoaded: result.documents.length,
                    hasMore: result.hasMore
                });

                setRows(result.documents);
                setLastDoc(result.lastDoc);
                setHasMore(result.hasMore);

                // 📊 Finalizar sesión
                tableProfiler.endSession({
                    documentsLoaded: result.documents.length,
                    hasActiveFilters: false,
                    success: true
                });
            }

        } catch (error) {
            console.error('Error en la búsqueda:', error);
            uploadErrorLogToFirestore('Error al buscar documentos: ' + error);
            setRows([]);
            setHasMore(false);
            
            // Finalizar sesión con error
            tableProfiler.endSession({
                error: error.message,
                success: false
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Limpia todos los filtros y recarga los documentos.
     */
    const handleClearFilters = () => {
        setClienteFilter("");
        setTipoFilter("");
        setTipFilter("");
        setFechaInicioFilter("");
        setFechaFinFilter("");

        // Recargar documentos sin filtros
        if (!isLoading && currentService?.id) {
            setIsLoading(true);
            setLoadingMessage("Cargando documentos...");

            fetchFilteredDocuments({
                serviceId: currentService.id,
                filters: {},
                pageSize: ITEMS_PER_PAGE,
                populateNombreFunc: populateNombre
            }).then(result => {
                setRows(result.documents);
                setLastDoc(result.lastDoc);
                setHasMore(result.hasMore);
                setCurrentPage(1);
            }).catch(error => {
                console.error('Error al limpiar filtros:', error);
                uploadErrorLogToFirestore('Error al limpiar filtros: ' + error);
            }).finally(() => {
                setIsLoading(false);
            });
        }
    }

    /**
     * Carga más documentos (paginación).
     */
    const handleLoadMore = useCallback(async () => {
        if (!isLoading && hasMore && lastDoc && currentService?.id) {
            setIsLoading(true);
            setLoadingMessage("Cargando más documentos...");

            // 📊 Iniciar sesión de profiling
            tableProfiler.startSession('TableDocuments_LoadMore', {
                serviceId: currentService.id,
                currentPage,
                currentDocuments: rows.length
            });

            try {
                // Crear objeto de filtros
                const filters = {
                    cliente: clienteFilter.trim(),
                    tipo: tipoFilter.trim(),
                    tip: tipFilter.trim(),
                    fechaInicio: fechaInicioFilter,
                    fechaFin: fechaFinFilter
                };

                // 📊 Medir carga de más documentos
                tableProfiler.startMeasurement('05_LoadMoreDocuments', {
                    pageSize: ITEMS_PER_PAGE,
                    currentPage: currentPage + 1
                });

                // Cargar más documentos
                const result = await fetchMoreFilteredDocuments({
                    serviceId: currentService.id,
                    filters,
                    lastDoc,
                    pageSize: ITEMS_PER_PAGE,
                    populateNombreFunc: populateNombre
                });

                tableProfiler.endMeasurement('05_LoadMoreDocuments', {
                    newDocuments: result.documents.length,
                    hasMore: result.hasMore
                });

                // Añadir a las filas existentes
                setRows(prevRows => [...prevRows, ...result.documents]);
                setLastDoc(result.lastDoc);
                setHasMore(result.hasMore);
                setCurrentPage(prev => prev + 1);

                // 📊 Finalizar sesión
                tableProfiler.endSession({
                    newDocuments: result.documents.length,
                    totalDocuments: rows.length + result.documents.length,
                    newPage: currentPage + 1,
                    success: true
                });

            } catch (error) {
                console.error('Error cargando más documentos:', error);
                uploadErrorLogToFirestore('Error al cargar más documentos: ' + error);
                setHasMore(false);
                
                // Finalizar sesión con error
                tableProfiler.endSession({
                    error: error.message,
                    success: false
                });
            } finally {
                setIsLoading(false);
            }
        }
    }, [isLoading, hasMore, lastDoc, clienteFilter, tipoFilter, tipFilter, fechaInicioFilter, fechaFinFilter, currentService?.id, populateNombre, currentPage, rows.length]);

    /**
     * Maneja la selección/deselección de todas las filas.
     * Solo selecciona las filas filtradas (visibles), no todas las filas.
     */
    const handleSelectAllRows = (event) => {
        if (event.target.checked) {
            // Solo seleccionar las filas filtradas (visibles), no todas las filas
            const filteredRowsIds = filteredRows.map((row) => row.id);
            setSelectedRows(filteredRowsIds);
            setAllRowsSelected(true);
        } else {
            setSelectedRows([]);
            setAllRowsSelected(false);
        }
    };

    /**
     * Maneja la selección/deselección de una fila.
     */
    const handleRowSelection = (event, id) => {
        if (event.target.checked) {
            setSelectedRows(prevSelected => [...prevSelected, id]);

            // Verificar si todas las filas filtradas están seleccionadas
            if (selectedRows.length + 1 === filteredRows.length) {
                setAllRowsSelected(true);
            }
        } else {
            setSelectedRows(prevSelected => prevSelected.filter(rowId => rowId !== id));
            setAllRowsSelected(false);

            // Verificar si la fila está marcada como borrada
            if (deletedRows.includes(id)) {
                setDeletedRows(prevDeleted => prevDeleted.filter(rowId => rowId !== id));
            }
        }
    };

    /**
     * Calcula el tiempo estimado restante basado en el progreso actual
     * @param {number} processedCount - Documentos procesados
     * @param {number} totalCount - Total de documentos
     * @param {number} startTime - Tiempo de inicio de la descarga
     * @returns {number} - Segundos restantes estimados
     */
    const calculateTimeRemaining = useCallback((processedCount, totalCount, startTime) => {
        if (processedCount === 0) return null;
        
        const elapsedMs = performance.now() - startTime;
        const msPerDoc = elapsedMs / processedCount;
        const remainingDocs = totalCount - processedCount;
        const remainingMs = msPerDoc * remainingDocs;
        
        return remainingMs / 1000; // Convertir a segundos
    }, []);

    /**
     * Actualiza las estadísticas de descarga
     * @param {Object} stats - Estadísticas actualizadas
     */
    const updateDownloadStats = useCallback((stats) => {
        setDownloadStats(prev => ({
            ...prev,
            ...stats
        }));
    }, []);

    /**
     * Maneja la descarga de documentos seleccionados.
     */
    const handleDownloadSelected = async () => {
        try {
            // Inicializar estado de descarga
            setDownloadProgress(0);
            setDownloadFeedback("Iniciando descarga...");
            setDownloadModalOpen(true);
            setTimeRemaining(null);
            setDownloadStats(null);
            
            // Guardar tiempo de inicio
            const downloadStartTime = performance.now();
            downloadStartTimeRef.current = downloadStartTime;

            if (!currentService?.id) {
                throw new Error("No se encontró el ID del servicio");
            }

            // Solo procesar documentos que están seleccionados Y visibles (filtrados)
            const selectedDocuments = filteredRows.filter((row) => selectedRows.includes(row.id));
            if (selectedDocuments.length === 0) {
                throw new Error("No hay documentos seleccionados");
            }

            // 📊 Iniciar sesión de profiling para la descarga
            tableProfiler.startSession('TableDocuments_Download', {
                serviceId: currentService.id,
                totalDocuments: selectedDocuments.length,
                userId: currentUser?.uid
            });

            // Inicializar estadísticas
            updateDownloadStats({
                processed: 0,
                total: selectedDocuments.length,
                speed: 0,
                currentPhase: 'Iniciando descarga...'
            });

            const zip = new JSZip();
            const folder = zip.folder('documentos');
            if (!folder) {
                throw new Error("No se pudo crear la carpeta ZIP");
            }

            const totalFiles = selectedDocuments.length;
            let downloadedFiles = 0;

            // Cache: un solo getDoc por tipo de documento (evita N llamadas iguales)
            const formCache = new Map();
            // CSV una sola vez por tipo (evita generar el mismo CSV cientos de veces)
            const csvTypesDone = new Set();

            // Atajo: si todos son del mismo tipo y solo exportan CSV, un solo CSV y listo (sin bucle)
            const uniqueTypes = [...new Set(selectedDocuments.map((d) => d.documentTypeId).filter(Boolean))];
            const singleTypeId = uniqueTypes.length === 1 ? uniqueTypes[0] : null;
            let fastPathDone = false;
            /** Descarga directa de CSV sin ZIP cuando hay muchos documentos (ej. monumentos 500+) para evitar horas de compresión */
            let directCSVDownloadDone = false;
            const CSV_DIRECT_DOWNLOAD_THRESHOLD = 100;
            if (singleTypeId && String(singleTypeId).trim() !== '') {
                try {
                    const formRef = doc(db, 'servicios', currentService.id, 'forms', String(singleTypeId));
                    const formSnapshot = await getDoc(formRef);
                    const formData = formSnapshot.exists() ? formSnapshot.data() : null;
                    formCache.set(singleTypeId, formData);
                    const exportTypes = formData?.export ? (Array.isArray(formData.export) ? formData.export : [formData.export]) : [];
                    const onlyCsv = exportTypes.length === 1 && exportTypes[0] === 'csv';
                    if (onlyCsv) {
                        updateDownloadStats({ currentPhase: 'Generando CSV...' });
                        setDownloadFeedback('Generando CSV...');
                        if (selectedDocuments.length >= CSV_DIRECT_DOWNLOAD_THRESHOLD) {
                            const result = getCSVBlobForDocuments(selectedDocuments, 'csv');
                            if (result) {
                                const link = document.createElement('a');
                                link.href = URL.createObjectURL(result.blob);
                                link.download = result.filename;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(link.href);
                                directCSVDownloadDone = true;
                                downloadedFiles = totalFiles;
                                fastPathDone = true;
                                setDownloadProgress(100);
                                updateDownloadStats({
                                    processed: totalFiles,
                                    speed: totalFiles,
                                    currentPhase: '¡Descarga completada!'
                                });
                            }
                        }
                        if (!directCSVDownloadDone) {
                            await downloadSelectedCSV(selectedDocuments, folder, 'csv');
                            downloadedFiles = totalFiles;
                            fastPathDone = true;
                            setDownloadProgress(100);
                            updateDownloadStats({
                                processed: totalFiles,
                                speed: totalFiles,
                                currentPhase: `Listo (${totalFiles} documentos). Generando ZIP...`
                            });
                        }
                    }
                } catch (_) { /* no usar atajo */ }
            }

            // 📊 Medir fase de procesamiento de documentos
            tableProfiler.startMeasurement('06_ProcessDocuments', {
                totalDocuments: totalFiles
            });

            const BATCH_SIZE = 50;
            let batchTimes = [];

            if (!fastPathDone) for (let i = 0; i < selectedDocuments.length; i += BATCH_SIZE) {
                const batch = selectedDocuments.slice(i, i + BATCH_SIZE);
                const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
                const totalBatches = Math.ceil(selectedDocuments.length / BATCH_SIZE);

                // 📊 Medir cada lote
                const batchStartTime = performance.now();
                tableProfiler.startMeasurement(`07_Batch_${batchNumber}`, {
                    batchSize: batch.length,
                    batchNumber,
                    totalBatches
                });

                updateDownloadStats({
                    currentPhase: `Procesando lote ${batchNumber} de ${totalBatches}`
                });
                setDownloadFeedback(`Procesando lote ${batchNumber} de ${totalBatches}`);

                await Promise.all(batch.map(async (document) => {
                    try {
                        let isCustomForm = true;
                        const typeId = document.documentTypeId;
                        let formData = typeId != null && String(typeId).trim() !== '' ? formCache.get(typeId) : undefined;
                        if (formData === undefined && typeId != null && String(typeId).trim() !== '') {
                            try {
                                const formRef = doc(db, 'servicios', currentService.id, 'forms', String(typeId));
                                const formSnapshot = await getDoc(formRef);
                                formData = formSnapshot.exists() ? formSnapshot.data() : null;
                                formCache.set(typeId, formData);
                            } catch (_) {
                                formData = null;
                                formCache.set(typeId, null);
                            }
                        }

                        if (formData?.export) {
                            const exportTypes = Array.isArray(formData.export) ? formData.export : [formData.export];
                            for (const exportType of exportTypes) {
                                try {
                                    if (exportType === "pdf") {
                                        await downloadSelectedDocumentsAsPDF(
                                            [document],
                                            folder,
                                            exportType,
                                            (doc) => getPdfDesignForDocument(currentService?.id, doc),
                                            formData
                                        );
                                    } else if (exportType === "csv") {
                                        if (!csvTypesDone.has(typeId)) {
                                            csvTypesDone.add(typeId);
                                            const sameTypeDocuments = selectedDocuments.filter((d) => d.documentTypeId === typeId);
                                            await downloadSelectedCSV(sameTypeDocuments, folder, exportType);
                                        }
                                    } else if (exportType === "foto") {
                                        await downloadSelectedPhotos([document], folder, exportType);
                                    }
                                } catch (exportError) {
                                    console.error(`Error procesando ${exportType} para documento ${document.id}:`, exportError);
                                }
                            }
                        } else {
                            isCustomForm = false;
                        }

                        if (!isCustomForm) {
                            if (document.documentTypeId === "112" && !csvTypesDone.has('112')) {
                                csvTypesDone.add('112');
                                const docs112 = selectedDocuments.filter((d) => d.documentTypeId === '112');
                                await downloadSelectedCSV(docs112, folder);
                            }
                            if (["legal", "ims", "denuncia", "intervencion"].includes(document.documentTypeId)) {
                                await downloadSelectedPhotos([document], folder);
                            }
                            const isParte = (document.documentTypeId && String(document.documentTypeId).toLowerCase().includes('parte')) ||
                                (document.documentTypeName && String(document.documentTypeName).toLowerCase().includes('parte'));
                            const isLegacyPdfType = ["parte de trabajo", "intervencion", "112", "parte_de_trabajo"].includes(document.documentTypeId);
                            if (isParte || isLegacyPdfType) {
                                await downloadSelectedDocumentsAsPDF(
                                    [document],
                                    folder,
                                    "pdf",
                                    (doc) => getPdfDesignForDocument(currentService?.id, doc)
                                );
                            }
                        }

                        downloadedFiles++;
                        // Throttle: actualizar UI como máximo cada 50 docs o 400ms para no bloquear con re-renders
                        const progress = (downloadedFiles / totalFiles) * 100;
                        const now = performance.now();
                        const last = lastProgressUpdateRef.current;
                        const shouldUpdate = downloadedFiles === totalFiles ||
                            downloadedFiles % 50 === 0 ||
                            !last.time ||
                            now - last.time >= 400;
                        if (shouldUpdate) {
                            lastProgressUpdateRef.current = { time: now, progress };
                            setDownloadProgress(progress);
                            const elapsedSeconds = (now - downloadStartTime) / 1000;
                            const speed = downloadedFiles / elapsedSeconds;
                            setTimeRemaining(calculateTimeRemaining(downloadedFiles, totalFiles, downloadStartTime));
                            updateDownloadStats({
                                processed: downloadedFiles,
                                speed,
                                currentPhase: `Procesando documento ${downloadedFiles} de ${totalFiles}`
                            });
                        }

                    } catch (error) {
                        console.error(`Error procesando documento ${document.id}:`, error);
                    }
                }));

                // 📊 Finalizar medición del lote
                const batchEndTime = performance.now();
                const batchDuration = batchEndTime - batchStartTime;
                batchTimes.push(batchDuration);
                
                tableProfiler.endMeasurement(`07_Batch_${batchNumber}`, {
                    documentsProcessed: batch.length,
                    durationMs: batchDuration,
                    avgTimePerDoc: batchDuration / batch.length
                });
            }

            // 📊 Finalizar medición de procesamiento de documentos
            tableProfiler.endMeasurement('06_ProcessDocuments', {
                documentsProcessed: downloadedFiles,
                totalBatches: Math.ceil(selectedDocuments.length / BATCH_SIZE),
                avgBatchTime: batchTimes.length > 0 ? batchTimes.reduce((a, b) => a + b, 0) / batchTimes.length : 0
            });

            let content = null;
            if (!directCSVDownloadDone) {
                // 📊 Medir generación del ZIP
                tableProfiler.startMeasurement('08_GenerateZIP', {
                    totalFiles: downloadedFiles
                });

                updateDownloadStats({
                    currentPhase: 'Generando archivo ZIP...'
                });
                setDownloadFeedback("Generando archivo ZIP...");
                setTimeRemaining(null);

                content = await zip.generateAsync({
                    type: "blob",
                    compression: "DEFLATE",
                    compressionOptions: {
                        level: 3
                    }
                });

                tableProfiler.endMeasurement('08_GenerateZIP', {
                    zipSizeBytes: content.size,
                    zipSizeMB: (content.size / (1024 * 1024)).toFixed(2)
                });

                const zipLink = document.createElement("a");
                zipLink.href = URL.createObjectURL(content);
                zipLink.download = `documentos_${new Date().toISOString().slice(0, 10)}.zip`;
                document.body.appendChild(zipLink);
                zipLink.click();
                document.body.removeChild(zipLink);
                URL.revokeObjectURL(zipLink.href);
            }

            const currentTimeStamp = new Date();

            // 📊 Calcular estadísticas y mostrar éxito de inmediato (no esperar a Firestore)
            const totalDownloadTimeMs = performance.now() - downloadStartTime;
            const totalDownloadTime = totalDownloadTimeMs / 1000;
            const finalSpeed = selectedDocuments.length / totalDownloadTime;

            // Mostrar éxito de inmediato (Firestore se actualiza en segundo plano)
            setRows(prevRows => prevRows.map(row => {
                if (selectedRows.includes(row.id) && filteredRows.some(filteredRow => filteredRow.id === row.id)) {
                    return { ...row, downloaded: true, downloadedTime: currentTimeStamp };
                }
                return row;
            }));
            setSelectedRows([]);
            setAllRowsSelected(false);
            setDownloadProgress(100);
            setTimeRemaining(0);
            updateDownloadStats({
                processed: selectedDocuments.length,
                speed: finalSpeed,
                currentPhase: '¡Descarga completada!'
            });
            setDownloadFeedback("Descarga completada con éxito");
            uploadLogToFirestore('Documentos descargados exitosamente');

            // Actualizar Firestore en segundo plano (no bloquea la UI)
            const updateBatchSize = 50;
            (async () => {
                tableProfiler.startMeasurement('09_UpdateFirestore', { documentsToUpdate: selectedDocuments.length });
                for (let i = 0; i < selectedDocuments.length; i += updateBatchSize) {
                    const updateBatch = selectedDocuments.slice(i, i + updateBatchSize);
                    await Promise.all(updateBatch.map(async (document) => {
                        try {
                            const docRef = doc(db, 'servicios', currentService.id, 'documents', document.id);
                            await updateDoc(docRef, { downloaded: true, downloadedTime: currentTimeStamp });
                        } catch (error) {
                            console.error(`Error actualizando estado de documento ${document.id}:`, error);
                        }
                    }));
                }
                tableProfiler.endMeasurement('09_UpdateFirestore', { documentsUpdated: selectedDocuments.length });
            })();

            // 📊 Calcular tiempos de cada fase para el log
            const processDocumentsTime = batchTimes.reduce((a, b) => a + b, 0);
            const avgBatchTime = batchTimes.length > 0 ? processDocumentsTime / batchTimes.length : 0;
            
            // 📊 Subir log de profiling a Firestore
            await uploadProfilingLogToFirestore({
                operation: 'download_documents',
                totalTimeMs: totalDownloadTimeMs,
                itemsProcessed: selectedDocuments.length,
                measurements: {
                    '01_ProcessDocuments': {
                        durationMs: processDocumentsTime,
                        description: 'Procesamiento de documentos (PDF, CSV, fotos)',
                        documentsProcessed: downloadedFiles,
                        totalBatches: Math.ceil(selectedDocuments.length / BATCH_SIZE),
                        avgBatchTimeMs: avgBatchTime
                    },
                    '02_GenerateZIP': {
                        durationMs: totalDownloadTimeMs - processDocumentsTime,
                        description: 'Generación del archivo ZIP',
                        zipSizeBytes: content?.size ?? 0,
                        zipSizeMB: content ? (content.size / (1024 * 1024)).toFixed(2) : '0'
                    },
                    '03_BatchDetails': {
                        totalBatches: batchTimes.length,
                        batchTimes: batchTimes.map((t, i) => ({
                            batch: i + 1,
                            durationMs: t.toFixed(0)
                        })),
                        fastestBatchMs: batchTimes.length ? Math.min(...batchTimes).toFixed(0) : '0',
                        slowestBatchMs: batchTimes.length ? Math.max(...batchTimes).toFixed(0) : '0'
                    }
                },
                metadata: {
                    serviceId: currentService.id,
                    userId: currentUser?.uid,
                    userEmail: currentUser?.email,
                    totalDocuments: selectedDocuments.length,
                    zipFileName: directCSVDownloadDone ? null : `documentos_${new Date().toISOString().slice(0, 10)}.zip`,
                    zipSizeMB: content ? (content.size / (1024 * 1024)).toFixed(2) : '0',
                    docsPerSecond: finalSpeed.toFixed(2),
                    avgTimePerDocMs: (totalDownloadTimeMs / selectedDocuments.length).toFixed(0),
                    batchSize: BATCH_SIZE,
                    timestamp: new Date().toISOString()
                }
            });

            // 📊 Finalizar sesión de profiling (consola)
            tableProfiler.endSession({
                totalDocuments: selectedDocuments.length,
                totalTimeSeconds: totalDownloadTime,
                avgTimePerDocument: totalDownloadTime / selectedDocuments.length,
                zipSizeMB: content ? (content.size / (1024 * 1024)).toFixed(2) : '0',
                docsPerSecond: finalSpeed,
                success: true
            });

        } catch (error) {
            console.error("Error en la descarga:", error);
            setDownloadFeedback(`Error: ${error.message}`);
            setTimeRemaining(null);
            updateDownloadStats({
                currentPhase: `Error: ${error.message}`
            });
            uploadErrorLogToFirestore(`Error general en la descarga: ${error.message}`);
            
            // 📊 Subir log de profiling con error
            const errorTimeMs = downloadStartTimeRef.current 
                ? performance.now() - downloadStartTimeRef.current 
                : 0;
            
            await uploadProfilingLogToFirestore({
                operation: 'download_documents_error',
                totalTimeMs: errorTimeMs,
                itemsProcessed: 0,
                measurements: {},
                metadata: {
                    serviceId: currentService?.id,
                    userId: currentUser?.uid,
                    userEmail: currentUser?.email,
                    error: error.message,
                    errorStack: error.stack,
                    timestamp: new Date().toISOString()
                }
            });
            
            // 📊 Finalizar sesión con error
            tableProfiler.endSession({
                error: error.message,
                success: false
            });
        } finally {
            setTimeout(() => {
                if (downloadFeedback && downloadFeedback.includes("éxito")) {
                    setDownloadModalOpen(false);
                }
            }, 3000);
        }
    };

    /**
     * Maneja la eliminación de documentos seleccionados.
     */
    const handleDeleteSelected = async () => {
        try {
            if (!currentService?.id) {
                throw new Error("No se encontró el ID del servicio");
            }

            if (selectedRows.length === 0) {
                return;
            }

            const currentTimeStamp = new Date();

            // Actualizar UI primero para respuesta inmediata
            // Solo actualizar las filas que están seleccionadas Y visibles
            setRows(prevRows => prevRows.map(row => {
                if (selectedRows.includes(row.id) && filteredRows.some(filteredRow => filteredRow.id === row.id)) {
                    return { ...row, borrado: true, borradoTime: currentTimeStamp };
                }
                return row;
            }));

            // Actualizar lista de filas borradas
            setDeletedRows(prevDeleted => [...prevDeleted, ...selectedRows]);

            // Desmarcar todas las filas seleccionadas
            setSelectedRows([]);
            setAllRowsSelected(false);

            // Actualizar documentos en la base de datos (en lotes)
            const BATCH_SIZE = 20;
            for (let i = 0; i < selectedRows.length; i += BATCH_SIZE) {
                const batch = selectedRows.slice(i, i + BATCH_SIZE);

                await Promise.all(batch.map(async (id) => {
                    try {
                        const docRef = doc(db, 'servicios', currentService.id, 'documents', id);
                        await updateDoc(docRef, {
                            borrado: true,
                            borradoTime: currentTimeStamp
                        });
                    } catch (error) {
                        console.error(`Error al marcar documento ${id} como borrado:`, error);
                        uploadErrorLogToFirestore(`Error al borrar documento ${id}: ${error.message}`);
                    }
                }));
            }

            uploadLogToFirestore(`${selectedRows.length} documentos marcados como borrados`);

        } catch (error) {
            console.error("Error al eliminar documentos:", error);
            uploadErrorLogToFirestore(`Error general al eliminar: ${error.message}`);
        }
    };

    /**
     * Filtra las filas para mostrar solo las no borradas.
     */
    const filteredRows = rows.filter(row => !deletedRows.includes(row.id));

    return (
        <div className="tableContainer">
            <h2 className="datatableTitle">Reportes</h2>

            <NewFilterBar
                clienteFilter={clienteFilter}
                setClienteFilter={setClienteFilter}
                tipoFilter={tipoFilter}
                setTipoFilter={setTipoFilter}
                tipFilter={tipFilter}
                setTipFilter={setTipFilter}
                fechaInicioFilter={fechaInicioFilter}
                setFechaInicioFilter={setFechaInicioFilter}
                fechaFinFilter={fechaFinFilter}
                setFechaFinFilter={setFechaFinFilter}
                handleSearch={handleSearch}
                handleClearFilters={handleClearFilters}
                isLoading={isLoading}
            />

            <DownloadModal
                isOpen={isDownloadModalOpen}
                onClose={() => setDownloadModalOpen(false)}
                progress={downloadProgress}
                feedback={downloadFeedback}
                timeRemaining={timeRemaining}
                downloadStats={downloadStats}
            />

            <div className="tableControlsRow">
                <ActionButtons
                    handleDownloadSelected={handleDownloadSelected}
                    selectedRows={selectedRows}
                    handleDeleteSelected={handleDeleteSelected}
                />
            </div>

            {isLoading ? (
                <div className="loading-container">
                    <CircularProgress size={40} />
                    <p className="loading-message">{loadingMessage}</p>
                </div>
            ) : filteredRows.length === 0 ? (
                <div className="no-results">
                    <p>No se encontraron documentos con los filtros aplicados.</p>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleClearFilters}
                    >
                        Limpiar filtros
                    </Button>
                </div>
            ) : (
                <>
                    <Table
                        filteredRows={filteredRows}
                        allRowsSelected={allRowsSelected}
                        selectedRows={selectedRows}
                        handleSelectAllRows={handleSelectAllRows}
                        handleRowSelection={handleRowSelection}
                        handleDeleteSelected={handleDeleteSelected}
                    />

                    {hasMore && (
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                            {/* <Button
                                onClick={handleLoadMore}
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <CircularProgress size={20} style={{ marginRight: '8px' }} color="inherit" />
                                        Cargando...
                                    </>
                                ) : (
                                    'Cargar más'
                                )}
                            </Button> */}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default TableDocuments;